import './App.css';
import React, {useEffect} from "react";
import {
    Button,
    Card,
    Form,
    Grid,
    Header,
    Icon,
    Input,
    Label,
    Popup,
    Ref,
    Segment,
    SegmentGroup,
    Sticky,
    Table,
    Transition,
} from 'semantic-ui-react';
import {ReactComponent as Cancel} from './assets/cancel.svg';
import Logo from "./assets/logo.png";
import {recordGAEvent, recordGAPageView} from "./googleAnalytics";

function App() {
    const originalCalculation = {
        emi: 0,
        loanPeriod: "-",
        loanAmount: 0,
        interestPayable: 0,
        totalPayment: 0,
        details: [],
    }
    const [values, setValues] = React.useState({
        loanAmount: '',
        interestRate: '',
        year: '',
        month: '',
    })
    const [calculation, setCalculation] = React.useState(originalCalculation)
    const [error, setError] = React.useState({
        loanAmountError: false,
        interestRateError: false,
        yearError: false,
        monthError: false,
        hasError: false,
        errorContext: undefined,
        errorFieldName: '',
    })
    const [openMoreDetails, setOpenMoreDetails] = React.useState(false)
    const [generatingPdf, setGeneratingPdf] = React.useState(false)
    const [generatedPdf, setPdfGenerated] = React.useState(false)
    const [calculatedDetails, setCalculatedDetails] = React.useState(false)

    let contextRef = React.createRef()
    let loanRef = React.useRef()
    let interestRef = React.useRef()
    let yearRef = React.useRef()
    let monthRef = React.useRef()

    useEffect(() => {
        recordGAPageView(document.title, window.location.href)
    }, [])

    function handleClick(ref) {
        ref.current.focus()
    }

    function validate(event) {
        if (generatedPdf) {
            setPdfGenerated(false)
        }
        if (generatingPdf) {
            setGeneratingPdf(false)
        }
        if (calculatedDetails) {
            setCalculatedDetails(false)
        }
        let {name, value, min, max} = event.target
        if (value && (value < parseInt(min) || value > parseInt(max))) {
            event.preventDefault()
            event.stopPropagation()
            error[`${name}Error`] = true

            // eslint-disable-next-line default-case
            switch (name) {
                case 'loanAmount':
                    error.errorContext = loanRef
                    error.errorFieldName = "Loan Amount"
                    break
                case 'interestRate':
                    error.errorContext = interestRef
                    error.errorFieldName = "Interest Rate"
                    break
                case 'year':
                    error.errorContext = yearRef
                    error.errorFieldName = "Year"
                    break
                case 'month':
                    error.errorContext = monthRef
                    error.errorFieldName = "Month"
                    break
            }
            error.hasError = true
            setError(error)
        } else {
            error.hasError = false
            error[`${name}Error`] = false
        }
        if (name === "interestRate") {
            values[name] = value ? parseFloat(value) : ''
        } else {
            values[name] = value ? parseInt(value) : ''
        }
        setValues(JSON.parse(JSON.stringify(values)))
        if (!error.hasError && (values.loanAmount && values.interestRate && (values.year || values.month))) {
            calculateEMI()
        } else {
            setCalculation(originalCalculation)
        }
    }

    function calculateEMI() {
        let chRate = (values.interestRate / 12) / 100
        let months = values.year ? values.year * 12 : 0
        months += values.month ? parseInt(values.month) : 0
        let emi = (
            (values.loanAmount * chRate * ((1 + chRate) ** months)) / ((1 + chRate) ** months - 1)
        ).toFixed(2)
        let totalPayment = (emi * months).toFixed(2)
        let interest = (totalPayment - values.loanAmount).toFixed(2)

        setCalculation({
            ...calculation,
            emi: emi,
            loanPeriod: months ? `${months} months` : "-",
            loanAmount: values.loanAmount,
            interestPayable: interest,
            totalPayment: totalPayment,
        })
        if (emi > 0) {
            recordGAEvent("calculation", "display", "", "")
        }
    }

    function calculateEMIDetails() {
        return new Promise((resolve) => {
            let chRate = (values.interestRate / 12) / 100
            let months = values.year ? values.year * 12 : 0
            months += values.month ? parseInt(values.month) : 0
            let emi = (
                (values.loanAmount * chRate * ((1 + chRate) ** months)) / ((1 + chRate) ** months - 1)
            ).toFixed(2)
            let detailAmount = values.loanAmount
            let details = []
            for (let month = 1; month <= months; month++) {
                let detailInterest = (chRate * detailAmount).toFixed(2)
                let detailPrincipal = (emi - detailInterest).toFixed(2)
                let detailBalance = (detailAmount - detailPrincipal).toFixed(2)
                details.push({
                    month: month,
                    interest: detailInterest,
                    principal: detailPrincipal,
                    totalPayable: emi,
                    balance: detailBalance,
                })
                detailAmount = detailBalance
            }
            calculation.details = details
            setCalculatedDetails(true)
            resolve()
        })
    }

    return (
        <div>
            <Popup
                context={error.errorContext}
                content={`${error.errorFieldName} too large`}
                position='top center'
                open={error.hasError}
            />
            {!openMoreDetails && (
                <SegmentGroup className="main-wrapper" compact raised>
                    <Segment className="header_wrapper" raised>
                        <div className="display_flex">
                            <img alt="EMI Calculator" className="header-logo" src={Logo}/>
                            <div className="title_logo_wrapper">EMI Calculator</div>
                        </div>
                    </Segment>
                    <Segment size="small" basic>
                        <Form>
                            <Form.Field error={error.loanAmountError}>
                                <Input type="number" labelPosition="right">
                                    <input ref={loanRef} name="loanAmount" min={0} max={999999999999}
                                           pattern="\d*"
                                           className="parameter-input"
                                           value={values.loanAmount}
                                           onInput={validate}/>
                                    <Label color={error.loanAmountError ? 'red' : undefined}
                                           className="loan-amount parameter-label"
                                           onClick={() => handleClick(loanRef)}>LOAN AMOUNT</Label>
                                </Input>
                            </Form.Field>
                            <Form.Field error={error.interestRateError}>
                                <Input type="number" labelPosition="right">
                                    <input ref={interestRef} name="interestRate" min={0} max={99}
                                           inputMode="decimal"
                                           pattern="\d*"
                                           className="parameter-input"
                                           value={values.interestRate}
                                           onInput={validate}/>
                                    <Label color={error.interestRateError ? 'red' : undefined}
                                           className="interest-rate parameter-label"
                                           onClick={() => handleClick(interestRef)}>INTEREST RATE %</Label>
                                </Input>
                            </Form.Field>
                            <Form.Group unstackable widths={2}>
                                <Form.Field error={error.yearError}>
                                    <Input type="number" labelPosition="right">
                                        <input ref={yearRef} name="year" min={0} max={100} value={values.year}
                                               pattern="\d*"
                                               className="parameter-input"
                                               onInput={validate}/>
                                        <Label color={error.yearError ? 'red' : undefined}
                                               className="year parameter-label"
                                               onClick={() => handleClick(yearRef)}>YEAR</Label>
                                    </Input>
                                </Form.Field>
                                <Form.Field error={error.monthError}>
                                    <Input type="number" labelPosition="right">
                                        <input ref={monthRef} name="month" min={0} max={12} value={values.month}
                                               pattern="\d*"
                                               className="parameter-input"
                                               onInput={validate}/>
                                        <Label color={error.monthError ? 'red' : undefined}
                                               className="month parameter-label"
                                               onClick={() => handleClick(monthRef)}>MONTH</Label>
                                    </Input>
                                </Form.Field>
                            </Form.Group>
                        </Form>
                    </Segment>
                    <Segment size="large" basic>
                        <Grid>
                            <Grid.Row verticalAlign="middle" style={{margin: ""}}>
                                <Grid.Column width={5}>
                                    <Header size="huge">
                                        <span id="emi-monthly-header">EMI</span>
                                        <Header.Subheader>Monthly</Header.Subheader>
                                    </Header>
                                </Grid.Column>
                                <Grid.Column width={11} textAlign="right">
                                    <Header id="monthly-emi" color="red" size="large">
                                        Rs. {Number(calculation.emi)?.toLocaleString('en-IN')}
                                    </Header>
                                </Grid.Column>
                            </Grid.Row>
                        </Grid>
                    </Segment>
                    <Segment size="small" basic>
                        <Card style={{background: "#F2F2F2"}} centered fluid>
                            <Card.Content style={{padding: '2.5em 0.5em'}}>
                                <Card.Header textAlign="center" id="loan-payment-card-header">LOAN PAYMENT</Card.Header>
                                <Card.Meta textAlign="center">Summary</Card.Meta>
                                <Grid padded>
                                    <Grid.Row columns="equal" verticalAlign="middle">
                                        <Grid.Column>
                                            <Header size="tiny" className="loan-payment-field-header">
                                                Loan Period
                                            </Header>
                                        </Grid.Column>
                                        <Grid.Column textAlign="right" className="loan-payment-field-value">
                                            {calculation.loanPeriod}
                                        </Grid.Column>
                                    </Grid.Row>
                                    <Grid.Row columns="equal" verticalAlign="middle">
                                        <Grid.Column>
                                            <Header size="tiny" className="loan-payment-field-header">
                                                Loan Amount
                                            </Header>
                                        </Grid.Column>
                                        <Grid.Column textAlign="right" className="loan-payment-field-value">
                                            Rs. {Number(calculation.loanAmount)?.toLocaleString('en-IN')}
                                        </Grid.Column>
                                    </Grid.Row>
                                    <Grid.Row columns="equal" verticalAlign="middle">
                                        <Grid.Column>
                                            <Header size="tiny" className="loan-payment-field-header">
                                                Interest Payable
                                            </Header>
                                        </Grid.Column>
                                        <Grid.Column textAlign="right" className="loan-payment-field-value">
                                            Rs. {Number(calculation.interestPayable)?.toLocaleString('en-IN')}
                                        </Grid.Column>
                                    </Grid.Row>
                                    <Grid.Row verticalAlign="middle">
                                        <Grid.Column width={6}>
                                            <Header size="tiny" className="loan-payment-field-header">
                                                Total Payment
                                            </Header>
                                        </Grid.Column>
                                        <Grid.Column width={10} textAlign="right" className="loan-payment-field-value">
                                            Rs. {Number(calculation.totalPayment)?.toLocaleString('en-IN')}
                                        </Grid.Column>
                                    </Grid.Row>
                                </Grid>
                            </Card.Content>
                        </Card>
                    </Segment>
                    <Segment size="small" basic className="display_flex emi_buttons">
                        <Button disabled={!calculation.emi}
                                onClick={async () => {
                                    calculateEMIDetails().then(() => {
                                        setOpenMoreDetails(true)
                                    })
                                    window.scrollTo(0, 0)
                                    recordGAEvent("details", "open", "loanPeriod", calculation.loanPeriod)
                                }}
                                size="medium" negative basic circular>
                            MORE DETAILS
                        </Button>
                    </Segment>
                </SegmentGroup>
            )}
            <div>
                {calculatedDetails && (
                    <Transition visible={openMoreDetails} animation="fly up" duration={{hide: 500, show: 500}} unmountOnHide>
                        <Ref innerRef={contextRef}>
                            <Segment
                                compact
                                basic
                                clearing
                                size="small"
                                style={{
                                    position: "absolute",
                                    top: 0,
                                    padding: 10,
                                    margin: 0,
                                }}>
                                <Sticky context={contextRef}>
                                    <Button icon labelPosition="left" size="medium" onClick={() => {
                                        setOpenMoreDetails(false)
                                        recordGAEvent("details", "close", "loanPeriod", calculation.loanPeriod)
                                    }}>
                                        <Icon style={{display: "flex", flexDirection: "row", justifyContent: "center"}}>
                                            <Cancel style={{fill: "#6b6b6b", width: "16"}}/>
                                        </Icon>
                                        Close
                                    </Button>
                                </Sticky>
                                <Table size="small"
                                       className="more-details-table"
                                       singleLine compact celled definition fixed collapsing unstackable striped>
                                    <Table.Header fullWidth>
                                        <Table.Row>
                                            <Table.HeaderCell style={{color: "#828282"}}>Month</Table.HeaderCell>
                                            <Table.HeaderCell style={{color: "#828282"}}>Interest</Table.HeaderCell>
                                            <Table.HeaderCell style={{color: "#828282"}}>Principal</Table.HeaderCell>
                                            <Table.HeaderCell style={{color: "#828282"}}>Total
                                                Payable</Table.HeaderCell>
                                            <Table.HeaderCell style={{color: "#EB5757"}}>Balance</Table.HeaderCell>
                                        </Table.Row>
                                    </Table.Header>
                                    <Table.Body>
                                        {calculation.details && calculation.details.map((detail, index) => {
                                            return (
                                                <Table.Row key={index}>
                                                    <Table.Cell width={3}>{detail.month}</Table.Cell>
                                                    <Table.Cell
                                                        style={{textAlign: "right"}}
                                                        width={3}>{Number(detail.interest)?.toLocaleString('en-IN')}</Table.Cell>
                                                    <Table.Cell
                                                        style={{textAlign: "right"}}
                                                        width={3}>{Number(detail.principal)?.toLocaleString('en-IN')}</Table.Cell>
                                                    <Table.Cell
                                                        style={{textAlign: "right"}}
                                                        width={3}>{Number(detail.totalPayable)?.toLocaleString('en-IN')}</Table.Cell>
                                                    <Table.Cell width={3}
                                                                style={{color: "#EB5757", textAlign: "right"}}>
                                                        {Number(detail.balance)?.toLocaleString('en-IN')}
                                                    </Table.Cell>
                                                </Table.Row>
                                            )
                                        })}
                                    </Table.Body>
                                </Table>
                            </Segment>
                        </Ref>
                    </Transition>)}
            </div>
        </div>
    );
}

export default App;
